import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'

interface CodeSnippetProps {
  code: string
  language?: string
  className?: string
  animateTyping?: boolean
  typingSpeed?: number
}

export default function CodeSnippet({
  code,
  language = 'typescript',
  className = '',
  animateTyping = false,
  typingSpeed = 30,
}: CodeSnippetProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [displayedCode, setDisplayedCode] = useState('')

  useEffect(() => {
    if (!isInView || !animateTyping) {
      setDisplayedCode(code)
      return
    }

    let currentIndex = 0
    const interval = setInterval(() => {
      if (currentIndex < code.length) {
        setDisplayedCode(code.slice(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(interval)
      }
    }, typingSpeed)

    return () => clearInterval(interval)
  }, [isInView, code, animateTyping, typingSpeed])

  // Syntax highlighting colors
  const getTokenColor = (char: string, index: number): string => {
    const colors = {
      keyword: '#0066CC', // Science Blue
      string: '#1D1D1F',
      comment: '#8E8E93',
      function: '#0066CC',
      default: 'var(--text-primary)',
    }

    // Simple syntax highlighting
    if (char.match(/[{}()[\];,]/)) return colors.default
    if (code.slice(index - 5, index).includes('function') || code.slice(index - 3, index).includes('const')) return colors.keyword
    if (char === "'" || char === '"') return colors.string
    
    return colors.default
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
      className={`font-mono text-sm w-full ${className}`}
    >
      <div className="neu p-4 rounded-xl overflow-hidden w-full">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
          <span className="ml-2 text-xs text-text-primary/50">{language}</span>
        </div>
        <pre className="overflow-x-auto w-full">
          <code className="text-text-primary w-full block">
            {displayedCode.split('').map((char, index) => (
              <span
                key={index}
                style={{
                  color: getTokenColor(char, index),
                  transition: 'color 0.2s',
                }}
              >
                {char}
              </span>
            ))}
            {animateTyping && displayedCode.length < code.length && (
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block w-2 h-4 bg-accent ml-1"
              />
            )}
          </code>
        </pre>
      </div>
    </motion.div>
  )
}

